import clsx from 'clsx';

/**
 * Formatting. Everything the storefront renders that is not a plain string
 * passes through here, so a price looks the same in a card, a cart line, and an
 * order confirmation.
 */

export const cn = (...parts) => clsx(...parts);

const rupees = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });
const rupeesWithPaise = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Money arrives from the API as integer paise. It becomes rupees exactly once,
 * here, at the moment a human reads it.
 */
export const formatMoney = (paise, { decimals = false, symbol = true } = {}) => {
  const value = (paise ?? 0) / 100;
  const formatted = decimals ? rupeesWithPaise.format(value) : rupees.format(value);
  return symbol ? `₹${formatted}` : formatted;
};

/** "₹2,499 – ₹4,199" for a variable product, "₹2,499" when every option matches. */
export const formatPriceRange = (range, fallback = 0) => {
  const min = range?.min ?? fallback;
  const max = range?.max ?? fallback;
  if (!min && !max) return formatMoney(fallback);
  if (min === max) return formatMoney(min);
  return `${formatMoney(min)} – ${formatMoney(max)}`;
};

export const discountPercent = (price, compareAt) => {
  if (!compareAt || compareAt <= price) return 0;
  return Math.round(((compareAt - price) / compareAt) * 100);
};

const dateFormatter = new Intl.DateTimeFormat('en-IN', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const dayFormatter = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short' });

export const formatDate = (value) => (value ? dateFormatter.format(new Date(value)) : '');

export const formatDay = (value) => (value ? dayFormatter.format(new Date(value)) : '');

/** "2 days ago" reads better than a date for anything inside a fortnight. */
export const formatRelative = (value) => {
  if (!value) return '';

  const diff = Date.now() - new Date(value).getTime();
  const days = Math.floor(diff / 86_400_000);

  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 14) return `${days} days ago`;
  return formatDate(value);
};

/** "Between 12 and 15 August" from the API's two-date estimate. */
export const formatDeliveryWindow = (dates) => {
  if (!dates?.length) return '';
  if (dates.length === 1) return `by ${formatDay(dates[0])}`;
  return `between ${formatDay(dates[0])} and ${formatDay(dates[1])}`;
};

export const pluralise = (count, singular, plural) =>
  `${count} ${count === 1 ? singular : (plural ?? `${singular}s`)}`;

/**
 * Builds the srcset/sizes pair for a product image. Every catalogue image
 * renders as a plain <img> with these — never through a blob URL, which would
 * forfeit HTTP caching, streaming decode, and priority hints.
 */
export const imageProps = (image, { sizes = '(min-width: 1024px) 33vw, 50vw', eager = false } = {}) => ({
  src: image?.url ?? '',
  srcSet: image?.srcset || undefined,
  sizes: image?.srcset ? sizes : undefined,
  alt: image?.alt ?? '',
  width: image?.width || undefined,
  height: image?.height || undefined,
  loading: eager ? 'eager' : 'lazy',
  decoding: eager ? 'sync' : 'async',
  fetchPriority: eager ? 'high' : undefined,
});

/** The blurred placeholder that holds an image's box while it decodes. */
export const lqipStyle = (image) =>
  image?.lqip
    ? {
        backgroundImage: `url(${image.lqip})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : undefined;

export const initials = (name) =>
  String(name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

export const truncate = (text, length = 120) => {
  const value = String(text ?? '');
  return value.length > length ? `${value.slice(0, length - 1).trimEnd()}…` : value;
};

/** Strips tags so rich text can be used as a meta description. */
export const plainText = (html, length) => {
  const text = String(html ?? '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return length ? truncate(text, length) : text;
};
